import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { subscribeWithSelector } from "zustand/middleware";
import sqliteStorage from "./sqliteStorage";

const DEFAULTS = {
  selectedReturnType: null,
  salesReturn: "N",
  salesReturnData: null,
  isCreditSale: null,       // stored as "true"/"false" string, compared with === "true"
  isB2bEnabled: null,       // stored as "true"/"false" string, compared with === "true"
  returnThisReceipt: null,  // stored as truthy value (true/string) or null
  refundCustomerEdit: null, // stored as "Y" or null, compared with === "Y"
  refundReturnCustomerId: null,
  walletPayments: [],
  couponAdditionalInfo: null,
  discountAppliedData: null,
  discountAdditionalInfo: null,
  billParkingData: null,
  orderType: null,
  // Active Sales-History date/search filter, stored as [searchKey, dateValue].
  // Kept separate from `orderType` (the sale's order type) so the sales-history
  // scroll pagination can re-issue the same query for the next page without
  // colliding with the order-type selection used by the sale flow.
  salesHistoryFilter: null,
  expiryDiscount: "N",
  approvalFlag: null,
  selectedPrintTemplateId: null,
  selectedRevenueCategoryId: null,
};

const useTransactionStore = create(
  subscribeWithSelector(
    persist(
      (set, get) => ({
        ...DEFAULTS,
        _hydrated: false,

        // Actions
        setSelectedReturnType: (type) => set({ selectedReturnType: type }),
        setSalesReturn: (val) => set({ salesReturn: val }),
        setSalesReturnData: (data) => set({ salesReturnData: data }),
        setIsCreditSale: (val) => set({ isCreditSale: val }),
        setIsB2bEnabled: (val) => set({ isB2bEnabled: val }),
        setReturnThisReceipt: (val) => set({ returnThisReceipt: val }),
        setRefundCustomerEdit: (val) => set({ refundCustomerEdit: val }),
        setRefundReturnCustomerId: (val) => set({ refundReturnCustomerId: val }),
        setWalletPayments: (payments) => set({ walletPayments: payments }),
        setCouponAdditionalInfo: (info) => set({ couponAdditionalInfo: info }),
        setDiscountAppliedData: (data) => set({ discountAppliedData: data }),
        setDiscountAdditionalInfo: (info) => set({ discountAdditionalInfo: info }),
        setBillParkingData: (data) => set({ billParkingData: data }),
        setOrderType: (type) => set({ orderType: type }),
        setSalesHistoryFilter: (filter) => set({ salesHistoryFilter: filter }),
        setExpiryDiscount: (val) => set({ expiryDiscount: val }),
        setApprovalFlag: (flag) => set({ approvalFlag: flag }),
        setSelectedPrintTemplateId: (id) => set({ selectedPrintTemplateId: id }),
        setSelectedRevenueCategoryId: (id) => set({ selectedRevenueCategoryId: id }),

        // selectedRevenueCategoryId is a till-session setting (not per-transaction), so preserve it across clears
        clearTransaction: () => set((state) => ({ ...DEFAULTS, selectedRevenueCategoryId: state.selectedRevenueCategoryId })),
      }),
      {
        name: "transactionStore",
        storage: createJSONStorage(() => sqliteStorage),
        skipHydration: true,
        partialize: (state) => {
          const { _hydrated, ...actions } = state;
          const persisted = {};
          for (const key of Object.keys(DEFAULTS)) {
            persisted[key] = actions[key];
          }
          return persisted;
        },
        merge: (persisted, current) => ({
          ...current,
          ...DEFAULTS,
          ...(persisted || {}),
          _hydrated: true,
        }),
      }
    )
  )
);

export default useTransactionStore;
